import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FiSave, FiRefreshCw, FiInfo, FiEye, FiEyeOff, FiPlus, FiTrash2, FiGlobe, FiEdit2, FiX } from "react-icons/fi";
import { toast } from "react-toastify";

const ElevenLabsSettings = ({ user }) => {
    const [config, setConfig] = useState({ apiKey: "", agentId: "" });
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchingCredits, setFetchingCredits] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showAgentId, setShowAgentId] = useState(false);
    const [voices, setVoices] = useState([]);
    const [newVoice, setNewVoice] = useState({ name: "", voiceId: "", language: "en", modelId: "turbo_v2_5", agentId: "" });
    const [addingVoice, setAddingVoice] = useState(false);
    const [editingVoice, setEditingVoice] = useState(null); // holds { _id, name, voiceId, language, modelId, agentId }
    const [savingVoice, setSavingVoice] = useState(false);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/elevenlabs/config`);
            setConfig({
                apiKey: response.data.apiKey || "",
                agentId: response.data.agentId || "",
            });
        } catch (error) {
            console.error("Error fetching config:", error);
            toast.error("Failed to load configuration.");
        }
        setLoading(false);
    };

    const fetchCredits = async () => {
        setFetchingCredits(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/elevenlabs/credits`);
            setCredits(response.data);
        } catch (error) {
            console.error("Error fetching credits:", error);
            // Don't toast error here as it might be because of missing key initially
        }
        setFetchingCredits(false);
    };

    const fetchVoices = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/voices`);
            setVoices(response.data);
        } catch (error) {
            console.error("Error fetching voices:", error);
        }
    };

    useEffect(() => {
        fetchConfig();
        fetchCredits();
        fetchVoices();
    }, []);

    const handleAddVoice = async (e) => {
        e.preventDefault();
        setAddingVoice(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER_API}/api/voices`, newVoice);
            setVoices([...voices, response.data]);
            setNewVoice({ name: "", voiceId: "", language: "en", modelId: "turbo_v2_5", agentId: "" });
            toast.success("Voice added successfully!");
        } catch (error) {
            console.error("Error adding voice:", error);
            toast.error("Failed to add voice.");
        }
        setAddingVoice(false);
    };

    const handleDeleteVoice = async (id) => {
        if (!window.confirm("Are you sure you want to delete this voice?")) return;
        try {
            await axios.delete(`${process.env.REACT_APP_SERVER_API}/api/voices/${id}`);
            setVoices(voices.filter(v => v._id !== id));
            toast.success("Voice deleted successfully!");
        } catch (error) {
            console.error("Error deleting voice:", error);
            toast.error("Failed to delete voice.");
        }
    };

    const handleStartEdit = (voice) => {
        setEditingVoice({ ...voice });
    };

    const handleSaveEdit = async () => {
        if (!editingVoice) return;
        setSavingVoice(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_SERVER_API}/api/voices/${editingVoice._id}`,
                editingVoice
            );
            setVoices(voices.map(v => v._id === editingVoice._id ? response.data : v));
            setEditingVoice(null);
            toast.success("Voice updated successfully!");
        } catch (error) {
            console.error("Error updating voice:", error);
            toast.error("Failed to update voice.");
        }
        setSavingVoice(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_SERVER_API}/api/elevenlabs/config`, {
                ...config,
                userId: user.id || user._id,
            });
            toast.success("Configuration updated successfully!");
            fetchCredits(); // Refresh credits with new key
        } catch (error) {
            console.error("Error updating config:", error);
            toast.error("Failed to update configuration.");
        }
        setLoading(false);
    };

    const [testingConnection, setTestingConnection] = useState(false);

    const testConnection = async () => {
        setTestingConnection(true);
        try {
            await axios.get('https://api.elevenlabs.io/v1/user', {
                headers: { 'xi-api-key': config.apiKey }
            });
            toast.success("Connection successful! API Key is valid.");
            fetchCredits();
        } catch (error) {
            console.error("Connection test failed:", error);
            toast.error("Connection failed. Please check your API Key.");
        }
        setTestingConnection(false);
    };

    if (loading && !config.apiKey) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="flex flex-col items-center gap-4">
                    <FiRefreshCw className="animate-spin text-5xl text-blue-500" />
                    <p className="text-gray-500 font-medium animate-pulse">Loading secure configuration...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-4 md:p-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-5xl mx-auto"
            >
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
                        AI Voice Settings
                    </h1>
                    <p className="text-gray-600 max-w-2xl">
                        Configure your AI Voice integration and monitor character usage across your workspace.
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
                    <motion.div
                        whileHover={{ y: -5 }}
                        className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <span className="p-2 bg-blue-50 text-blue-600 rounded-lg"><FiRefreshCw size={20} className={fetchingCredits ? "animate-spin" : ""} /></span>
                            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full uppercase">Remaining</span>
                        </div>
                        <div>
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {fetchingCredits ? "---" : credits?.remaining?.toLocaleString() || "0"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">Characters available</p>
                        </div>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <span className="p-2 bg-green-50 text-green-600 rounded-lg"><FiInfo size={20} /></span>
                            <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full uppercase">Total Plan</span>
                        </div>
                        <div>
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                                {fetchingCredits ? "---" : credits?.character_limit?.toLocaleString() || "0"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">Monthly character limit</p>
                        </div>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <span className="p-2 bg-orange-50 text-orange-600 rounded-lg"><FiSave size={20} /></span>
                            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-full uppercase">Usage</span>
                        </div>
                        <div>
                            <div className="flex items-end gap-2">
                                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                                    {credits ? Math.round((credits.character_count / credits.character_limit) * 100) : "0"}%
                                </h3>
                                <div className="flex-1 h-3 bg-gray-100 rounded-full mb-2 overflow-hidden">
                                    <div
                                        className="h-full bg-orange-500 rounded-full transition-all duration-1000"
                                        style={{ width: `${credits ? (credits.character_count / credits.character_limit) * 100 : 0}%` }}
                                    ></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Character consumption rate</p>
                        </div>
                    </motion.div>
                </div>

                {/* Configuration Form */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-8 md:p-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">API Configuration</h2>
                                <p className="text-sm text-gray-500 mt-1">Keep your keys private and update them as needed.</p>
                            </div>
                            <button
                                type="button"
                                onClick={testConnection}
                                disabled={testingConnection || !config.apiKey}
                                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                            >
                                {testingConnection ? <FiRefreshCw className="animate-spin" /> : <FiInfo />}
                                {testingConnection ? "Testing..." : "Verify Connection"}
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">XI-API-KEY</label>
                                    <div className="relative group">
                                        <input
                                            type={showApiKey ? "text" : "password"}
                                            value={config.apiKey}
                                            onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                                            placeholder="Paste your API key"
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all pr-14 text-gray-900 font-medium"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowApiKey(!showApiKey)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                                        >
                                            {showApiKey ? <FiEyeOff size={22} /> : <FiEye size={22} />}
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-gray-400 ml-1">Your secure key remains encrypted in our database.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">Conversational Agent ID</label>
                                    <div className="relative group">
                                        <input
                                            type={showAgentId ? "text" : "password"}
                                            value={config.agentId}
                                            onChange={(e) => setConfig({ ...config, agentId: e.target.value })}
                                            placeholder="Paste your Agent ID"
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all pr-14 text-gray-900 font-medium"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowAgentId(!showAgentId)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                                        >
                                            {showAgentId ? <FiEyeOff size={22} /> : <FiEye size={22} />}
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-gray-400 ml-1">Locate this in your VoiceAI Dashboard.</p>
                                </div>
                            </div>

                            <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                                <div className="flex items-start gap-3 bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50 flex-1 max-w-lg">
                                    <FiInfo className="text-blue-500 mt-1 shrink-0" size={18} />
                                    <span className="text-xs text-blue-700 font-medium leading-relaxed">
                                        Saving these changes will immediately update all active and future AI voice interactions. Ensure your keys are correct before deployment.
                                    </span>
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 active:scale-95 transition-all shadow-xl shadow-blue-200 disabled:opacity-50 disabled:scale-100 min-w-[200px]"
                                >
                                    {loading ? <FiRefreshCw className="animate-spin" /> : <FiSave size={20} />}
                                    {loading ? "Updating..." : "Save Settings"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Voice Management Section */}
                <div className="mt-8 bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-8 md:p-10">
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-gray-900">Voice Management</h2>
                            <p className="text-sm text-gray-500 mt-1">Define voices that clients can use in their call configurations.</p>
                        </div>

                        {/* Add Voice Form */}
                        <form onSubmit={handleAddVoice} className="mb-12 p-10 bg-gray-50/50 rounded-[2.5rem] border border-gray-100/80 shadow-inner">
                            <div className="flex items-center gap-3 mb-8 ml-1">
                                <div className="p-2 bg-blue-600 rounded-lg text-white">
                                    <FiPlus size={16} />
                                </div>
                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Add New Configuration</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                                <div className="md:col-span-5 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Voice Name</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={newVoice.name}
                                            onChange={(e) => setNewVoice({ ...newVoice, name: e.target.value })}
                                            placeholder="e.g. Hindi Female Premium"
                                            className="w-full pl-5 pr-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-sm transition-all shadow-sm placeholder:text-gray-300 font-medium"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-7 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Voice ID</label>
                                    <input
                                        type="text"
                                        value={newVoice.voiceId}
                                        onChange={(e) => setNewVoice({ ...newVoice, voiceId: e.target.value })}
                                        placeholder="Paste voice ID here..."
                                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-xs font-mono transition-all shadow-sm placeholder:text-gray-300"
                                        required
                                    />
                                </div>

                                <div className="md:col-span-3 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Language</label>
                                    <input
                                        type="text"
                                        value={newVoice.language}
                                        onChange={(e) => setNewVoice({ ...newVoice, language: e.target.value.toLowerCase() })}
                                        placeholder="hi, en, es"
                                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-sm transition-all shadow-sm text-center font-bold"
                                        required
                                    />
                                </div>

                                <div className="md:col-span-4 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Model Engine</label>
                                    <input
                                        type="text"
                                        value={newVoice.modelId}
                                        onChange={(e) => setNewVoice({ ...newVoice, modelId: e.target.value })}
                                        placeholder="voiceai_v3"
                                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-xs font-mono transition-all shadow-sm"
                                        required
                                    />
                                </div>

                                <div className="md:col-span-5 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        Agent ID
                                        <span className="bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded text-[8px]">OPTIONAL</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newVoice.agentId}
                                        onChange={(e) => setNewVoice({ ...newVoice, agentId: e.target.value })}
                                        placeholder="Use specific agent..."
                                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none text-xs font-mono transition-all shadow-sm"
                                    />
                                </div>

                                <div className="md:col-span-12 pt-2">
                                    <button
                                        type="submit"
                                        disabled={addingVoice}
                                        className="w-full py-4 bg-gray-900 text-white text-sm font-black rounded-2xl hover:bg-blue-600 active:scale-[0.99] transition-all shadow-xl shadow-gray-200 flex items-center justify-center gap-3 uppercase tracking-widest"
                                    >
                                        {addingVoice ? <FiRefreshCw className="animate-spin" size={18} /> : (
                                            <>
                                                <FiPlus size={20} />
                                                Confirm & Create Voice
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <p className="mt-4 text-[10px] text-gray-400 flex items-center gap-1.5 ml-1">
                                <FiInfo size={12} className="text-blue-400" />
                                Models: <code className="bg-gray-100 px-1 rounded mx-1">voice_ai_v3</code> (75+ langs), <code className="bg-gray-100 px-1 rounded mx-1">voiceai_turbo_v2_5</code>, <code className="bg-gray-100 px-1 rounded mx-1">voiceai_flash_v2_5</code>. Agent ID is optional — leave blank to use global agent.
                            </p>
                        </form>

                        {/* Voice List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {voices.length === 0 ? (
                                <div className="md:col-span-2 text-center py-12 text-gray-400 bg-gray-50/30 rounded-3xl border border-dashed border-gray-100">
                                    <p className="text-sm italic">No custom voices added yet.</p>
                                </div>
                            ) : (
                                voices.map((voice) => (
                                    <div key={voice._id} className="p-5 bg-white border border-gray-100 rounded-3xl hover:border-blue-100 hover:shadow-xl hover:shadow-gray-100/50 transition-all group relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-1 h-full bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />

                                        {editingVoice?._id === voice._id ? (
                                            <div className="space-y-5 p-2 bg-blue-50/20 rounded-2xl">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="p-1.5 bg-blue-600 rounded text-white shadow-sm">
                                                        <FiEdit2 size={12} />
                                                    </div>
                                                    <h5 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Editing Mode</h5>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="col-span-2 space-y-1">
                                                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Friendly Name</label>
                                                        <input
                                                            type="text"
                                                            value={editingVoice.name}
                                                            onChange={(e) => setEditingVoice({ ...editingVoice, name: e.target.value })}
                                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-xs font-semibold text-gray-700 shadow-sm"
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Language</label>
                                                        <input
                                                            type="text"
                                                            value={editingVoice.language}
                                                            onChange={(e) => setEditingVoice({ ...editingVoice, language: e.target.value.toLowerCase() })}
                                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-xs font-bold text-center"
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Model</label>
                                                        <input
                                                            type="text"
                                                            value={editingVoice.modelId}
                                                            onChange={(e) => setEditingVoice({ ...editingVoice, modelId: e.target.value })}
                                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-[10px] font-mono"
                                                        />
                                                    </div>
                                                    <div className="col-span-2 space-y-1">
                                                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Voice ID</label>
                                                        <input
                                                            type="text"
                                                            value={editingVoice.voiceId}
                                                            onChange={(e) => setEditingVoice({ ...editingVoice, voiceId: e.target.value })}
                                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-[10px] font-mono"
                                                        />
                                                    </div>
                                                    <div className="col-span-2 space-y-1">
                                                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Specific Agent ID</label>
                                                        <input
                                                            type="text"
                                                            value={editingVoice.agentId}
                                                            onChange={(e) => setEditingVoice({ ...editingVoice, agentId: e.target.value })}
                                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none text-[10px] font-mono"
                                                            placeholder="Global Fallback"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 justify-end pt-4 border-t border-gray-100">
                                                    <button
                                                        onClick={() => setEditingVoice(null)}
                                                        className="px-5 py-2.5 text-[11px] font-bold text-gray-500 hover:text-gray-900 transition-all uppercase tracking-widest"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        disabled={savingVoice}
                                                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-[11px] font-black rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 active:scale-95 disabled:opacity-50 uppercase tracking-widest"
                                                    >
                                                        {savingVoice ? <FiRefreshCw className="animate-spin" size={12} /> : <FiSave size={14} />}
                                                        Save Changes
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3.5 bg-gray-50 text-gray-400 rounded-2xl group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                                        <FiGlobe size={20} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 flex items-center gap-2">
                                                            {voice.name}
                                                            <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-black tracking-widest uppercase">{voice.language}</span>
                                                        </h4>
                                                        <p className="text-[10px] font-mono text-gray-400 mt-0.5">{voice.modelId || 'voiceai_turbo_v2_5'}</p>
                                                        <p className="text-[10px] font-mono text-gray-400 truncate max-w-[160px]">{voice.voiceId}</p>
                                                        {voice.agentId && (
                                                            <p className="text-[9px] font-mono text-emerald-500 mt-0.5 truncate max-w-[160px]">agent: {voice.agentId}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity absolute top-5 right-5">
                                                    <button
                                                        onClick={() => handleStartEdit(voice)}
                                                        className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-100/50"
                                                        title="Edit Voice"
                                                    >
                                                        <FiEdit2 size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteVoice(voice._id)}
                                                        className="p-2.5 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-xl transition-all active:scale-95 shadow-lg shadow-red-100/50"
                                                        title="Delete Voice"
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ElevenLabsSettings;
